/**
 * Analytics events for the protocol OBJECT — structuring a written procedure
 * into steps, correcting what the extractor typed, and binding media to a step.
 *
 * One of the `analytics-events/` modules `analytics.ts` spreads together, so
 * every call site still reads `ANALYTICS_EVENTS.PROTOCOL_VALUE_CORRECTED`.
 *
 * `as const` is load-bearing: spread into another `as const` object it keeps
 * the literal types, so `AnalyticsEvent` stays a union of exact strings rather
 * than widening to `string`.
 *
 * The properties rule for every event below is the same one the rest of the
 * file follows, and it matters more here than almost anywhere else: a protocol
 * is the researcher's own document. Emit ids, roles, counts and booleans. Never
 * a step title, never a source phrase, never the number a value carried
 * (`rules/analytics.md`, `rules/compliance.md`).
 */
export const PROTOCOL_OBJECT_EVENTS = {
  // A person asking Bower to read their protocol's text again after its first
  // derive stopped with no steps (Try again on the protocol page). Fires when
  // the ENQUEUE is accepted, not on click. Properties: `protocol_id` and
  // `entry_point` (`retry`) only, never the text. The outcome is emitted
  // server-side.
  PROTOCOL_STRUCTURE_REQUESTED: 'Protocol Structure Requested',
  // The same stopped protocol turned back into the note it was made from (Keep
  // it as a note). How often a person's own text is not one Bower can lay out
  // as steps. Properties: `protocol_id` only.
  PROTOCOL_KEPT_AS_NOTE: 'Protocol Kept As Note',
  // Correcting the extractor. "How often is a value wrong" is the single most
  // valuable signal this feature emits: it is the measurement that says
  // whether the extraction prompt is good enough to widen the rollout.
  // Properties are what was touched (`corrected_fields`), never the number
  // itself and never the source wording, both of which are the researcher's
  // document. It no longer says which kind of value: the role is retired
  // (2026-10-13), and events before that date carry `value_role`.
  PROTOCOL_VALUE_CORRECTED: 'Protocol Value Corrected',
  // The extractor typed something that was not a value at all. A different
  // failure from a wrong number, and the one that means the prompt is
  // over-reaching rather than mis-reading.
  PROTOCOL_VALUE_REVERTED: 'Protocol Value Reverted',
  // A person promoted a run of prose the extractor left as prose. The third
  // reading of the same question the other two ask: a correction says the
  // model read a value WRONG, a revert says it saw one that was not there,
  // and this says it MISSED one - which neither of the others can report. A
  // protocol accumulating these is a prompt that is under-reaching.
  PROTOCOL_VALUE_ADDED: 'Protocol Value Added',
  PROTOCOL_STEP_EDITED: 'Protocol Step Edited',
  // Two people wrote one step's prose at once, and this person had to choose.
  // `resolution` is one of `review`,
  // `keep_mine`, `use_theirs` or `abandoned` (left the page still held).
  // `review` is NOT terminal: it fires when the comparison opens, and the
  // person then keeps or takes. Count conflicts on the other three. How often
  // this fires is what sizes real-time co-editing; ids only, never the text.
  PROTOCOL_STEP_SAVE_CONFLICTED: 'Protocol Step Save Conflicted',
  // The editor kept something from the server it knows would be refused, so
  // no request (and no backend log line) records it. `reason` is
  // `empty_body` (a save held back because the step would be left empty),
  // `foreign_chip` (a pasted chip from another step came in as words) or
  // `copied_chip` (a copy of this step's own chip, pasted beside the
  // original, came in as words). Ids only, never the text.
  PROTOCOL_STEP_SAVE_HELD: 'Protocol Step Save Held',
  // A block a person inserted into a step body from the insert menu. The
  // property is the KIND only (`table` or `block_math`); never the content.
  // It answers a question the import counts cannot: whether researchers ADD
  // structure the source document never had, which is the difference between
  // a body schema that merely preserves what was imported and one people
  // write in.
  PROTOCOL_STEP_BLOCK_INSERTED: 'Protocol Step Block Inserted',
  // Adding and removing a step, and the rail sections' own edits. Together
  // these answer the question the correction events cannot: is a person
  // FIXING what the extractor produced, or writing the protocol themselves?
  // A draft that gains ten steps by hand means something very different for
  // the extraction prompt than one where every step was corrected in place.
  // Counts and ids only - never a title, and never which section's prose
  // changed beyond its name.
  PROTOCOL_STEP_ADDED: 'Protocol Step Added',
  PROTOCOL_STEP_REMOVED: 'Protocol Step Removed',
  PROTOCOL_SECTION_EDITED: 'Protocol Section Edited',
  // The rail's own shape: which sections a protocol keeps, which it drops
  // (Recipes is usually empty), which the author invents, and whether anybody
  // reorders them. Properties: `protocol_id` and `section_kind` only
  // (`acceptance_criteria`, `safety`, ..., `custom`). Never a custom
  // section's title: it is the researcher's own words.
  PROTOCOL_SECTION_ADDED: 'Protocol Section Added',
  PROTOCOL_SECTION_REMOVED: 'Protocol Section Removed',
  PROTOCOL_SECTIONS_REORDERED: 'Protocol Sections Reordered',
  // A parameter's type (options, range, text) switched by the author: how
  // often the extractor's guess at a parameter's kind is wrong. Properties:
  // `protocol_id`, `from_kind`, `to_kind`; never the parameter's name.
  PROTOCOL_PARAMETER_TYPE_CHANGED: 'Protocol Parameter Type Changed',
  PROTOCOL_STEP_REORDERED: 'Protocol Step Reordered',
  // Sections COMBINED, which is a different signal from either editing one or
  // removing one. It is the clearest evidence there is that derivation
  // over-split a paper's method: nobody merges sections a person wrote. Read
  // against `PROTOCOL_SECTION_EDITED`, a rising merge rate says the extraction
  // prompt is cutting where the document does not. Step counts only - a
  // section title is the researcher's own words.
  PROTOCOL_SECTION_MERGED: 'Protocol Section Merged',
  PROTOCOL_SECTION_MERGE_UNDONE: 'Protocol Section Merge Undone',
  PROTOCOL_VERSION_FROZEN: 'Protocol Version Frozen',
  // Revising an approved protocol. The pair with the freeze event is what says
  // whether protocols are LIVING documents in a lab or write-once imports -
  // which is the question the whole versioning design turns on.
  PROTOCOL_VERSION_CREATED: 'Protocol Version Created',
  // A version deleted from the kebab: a draft nobody wanted, or a superseded
  // version no run followed. Read against CREATED: how often a draft is
  // abandoned. { protocol_id, version_number, was_draft }
  PROTOCOL_VERSION_DELETED: 'Protocol Version Deleted',
  // The page switched to another version, fired once the new version is on
  // screen (not on the click, which the unsaved-step guard can hold). Source:
  // the not-current notice's "Go to" link (`banner`), the current version's
  // "Open vN" link to a draft in progress (`draft_notice`) or the rail's
  // Lineage rows (`rail`). Says which way in people use and whether older
  // versions are read at all. `header_menu` (the header's version menu,
  // removed in #4182) appears only in events from builds before that change
  // was released.
  // { protocol_id, source, from_version, to_version }
  PROTOCOL_VERSION_SWITCHED: 'Protocol Version Switched',
  // Media bound to a STEP rather than to a protocol by timestamp. Properties
  // are the role and whether it is a clip - never the caption, which is the
  // researcher's own words.
  PROTOCOL_STEP_MEDIA_ATTACHED: 'Protocol Step Media Attached',
  PROTOCOL_STEP_MEDIA_DETACHED: 'Protocol Step Media Detached',
  // What a run will ask the person to capture on a step, set or cleared by
  // hand. Properties: `protocol_id`, `capture_kind` (`photo` | `video` |
  // `note` | `none`) and `previous_kind` (the same vocabulary). Paired with the
  // derivation event's `steps_capture_*` counts, it says how often a person
  // adds, corrects or removes what the import tagged.
  PROTOCOL_STEP_CAPTURE_SET: 'Protocol Step Capture Set',
  // A step's type (Do, Wait, Hold, Check, Note) changed by hand. Properties:
  // `protocol_id`, `step_kind` and `previous_kind` (`action` | `wait` |
  // `hold` | `gate` | `note`). On imported steps, says how often the import's
  // guess is corrected.
  PROTOCOL_STEP_KIND_SET: 'Protocol Step Kind Set',
  // Somebody opened a step's picture or clip full-size. The pair with
  // ATTACHED is what says whether step media is LOOKED AT at a bench or just
  // filed - which is the question the whole step-binding design turns on.
  PROTOCOL_STEP_MEDIA_OPENED: 'Protocol Step Media Opened',
  // "Execute protocol" on the phone protocol page, which opens a live session
  // that starts this protocol in code. The web twin of the native app's
  // `protocol_execute_clicked`. Fires on the tap: the tap IS the action, and
  // whether the run started is the agent service's own start event.
  // Properties: `protocol_id` and `source` (`protocol_detail`) only.
  PROTOCOL_EXECUTE_CLICKED: 'Protocol Execute Clicked',
  // "Run on your phone" on the desktop protocol page opened its QR popover:
  // runs start on the phone, so this is desktop's way to one. Fires on open.
  // Properties: `protocol_id` only.
  PROTOCOL_RUN_ON_PHONE_OPENED: 'Protocol Run On Phone Opened',
  // Protocol runs inside Protocols (spec 2026-10-15-protocol-runs-layout).
  // Ids, counts and booleans only: never a step title or a capture's text.
  // A protocol's runs expanded or collapsed: on /protocols (`surface: 'list'`)
  // or the protocol page rail's Runs section (`surface: 'rail'`).
  // { protocol_id, run_count, surface }
  PROTOCOL_RUNS_EXPANDED: 'Protocol Runs Expanded',
  PROTOCOL_RUNS_COLLAPSED: 'Protocol Runs Collapsed',
  // "Show 10 more" under an expanded protocol. { protocol_id, loaded }
  PROTOCOL_RUNS_MORE_LOADED: 'Protocol Runs More Loaded',
  // A run record opened. { protocol_id, run_id, from: 'list' | 'rail' | 'redirect' }
  PROTOCOL_RUN_OPENED: 'Protocol Run Opened',
  // A capture opened from a run record. { run_id, artifact_type }
  PROTOCOL_RUN_EVIDENCE_OPENED: 'Protocol Run Evidence Opened',
  // A note added from a run record's "Add a note", once saved. Never the
  // text. { run_id, dictated, ran_it, filing: 'filed' | 'fallback',
  // filing_reason: a LIVE_FILING_FALLBACK_REASONS value | 'client_failed' | null }
  PROTOCOL_RUN_NOTE_ADDED: 'Protocol Run Note Added',
  // "View analysis" in a run's summary, jumping to its Run analysis. { run_id }
  PROTOCOL_RUN_ANALYSIS_VIEWED: 'Protocol Run Analysis Viewed',
  // "Show more" on a long note in a run's Run analysis. { run_id }
  PROTOCOL_RUN_NOTE_EXPANDED: 'Protocol Run Note Expanded',
  // The "Active runs" toggle on /protocols (labeled "Open runs" before
  // 2026-10-18; the event name is unchanged). { on }
  PROTOCOL_RUNS_OPEN_FILTER_TOGGLED: 'Protocol Runs Open Filter Toggled',
  // "View runs" in the structured protocol's kebab (flag on; replaces "View
  // captures" there). { protocol_id }
  PROTOCOL_VIEW_RUNS_CLICKED: 'Protocol View Runs Clicked',
  // Rail Runs section: this version vs all versions. { scope }
  PROTOCOL_RUNS_VERSION_SCOPE_CHANGED: 'Protocol Runs Version Scope Changed',
  // Rail Runs section: "View all in Protocols". { protocol_id }
  PROTOCOL_RUNS_VIEW_ALL_CLICKED: 'Protocol Runs View All Clicked',
  // Rail Lineage: "Show N earlier versions" opened the versions folded away
  // in a long history. { hidden_count }
  PROTOCOL_LINEAGE_EXPANDED: 'Protocol Lineage Expanded',
  // "Show all protocols" under "This protocol isn't available to you", when
  // the Protocol filter alone named a protocol the reader cannot see (deleted,
  // no access): clears that filter. `found` is false since 2026-10-18, when
  // the one-protocol banner it also counted gave way to the Protocol filter
  // chip (that chip's changes are LIST_FILTERED). { found }
  PROTOCOL_RUNS_SHOW_ALL_CLICKED: 'Protocol Runs Show All Clicked',
} as const;
